import axios from "axios";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import useLanguage from "../../../utils/useLanguage";

import CustomButton from "../../../components/CustomButton";
import Modal from "../../../components/Modal";
import classes from "../../../components/Modal.module.css";
import Dropdown from "../../../components/Dropdown";
import CustomInput from "../../../components/CustomInput";
import ItemInvoiceCard from "../ItemInvoiceCard";
import SectionDivider from "../../../components/SectionDivider";
import BalanceBoxes from "../../../components/BalanceBoxes";
import CustomInputWithUnit from "../../../components/CustomInputWithUnit";


import { DataContext } from "../../../utils/DataContext";
import { UserAuth } from "../../../utils/AuthContext";

import { formatDate } from "../../../utils/toDate";
import { toMoney } from "../../../utils/toMoney";
import { sanitizeNumber } from "../../../utils/sanitizeNumber";
import { onlyNums } from "../../../utils/OnlyNums";

import { toWords } from "number-to-words";
import { toArabicWord } from "number-to-arabic-words/dist/index-node.js";
import Loader from "../../../components/Loader";
import { useNavigate } from "react-router-dom";
import AreYouSureModal from "../../../components/AreYouSureModal";
import { shouldCloseModal } from "../../../utils/shouldCloseModal";
import { calculatePercentage } from "../../../utils/calculatePercentage";
import HotCustomInput from "../../../components/HotCustomInput";
import ErrorBox from "../../../components/ErrorBox";

export default function EditItemInvoiceModal({
  handleToggleModal,
  data,
  setData,
}) {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const navigate = useNavigate();

  const { getCustomers } = useContext(DataContext);

  const { userData } = useContext(UserAuth);

  const [selectedCustomer, setSelectedCustomer] = useState({
    id: data?.customer?.id,
    name: data?.customer?.name,
    phoneNumber: data?.customer?.phoneNumber,
    address: data?.customer?.address,
  });

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
  };

  const [invoice, setInvoice] = useState({
    id: data?.id,
    date: data?.date,
    discount: data?.discount,
    receivedAmount: data?.receivedAmount,
    leftAmount: data?.leftAmount,
    notes: data?.notes,
    items: data?.items,
  });

  const [amounts, setAmounts] = useState({
    totalPrice: data?.totalPrice,
    finalAmount: data?.finalAmount,
    finalAmountInAr: data?.finalAmountInAr,
    finalAmountInEn: data?.finalAmountInEn,
  });

  const [selectedInvoiceType, setSelectedInvoiceType] = useState({
    title: t(data?.invoiceType),
    value: data?.invoiceType,
  });

  const [loading, setLoading] = useState(false);
  const [loadingInput, setLoadingInput] = useState(false);
  const [error, setError] = useState("");

  const [showAreYouSure, setShowAreYouSure] = useState(false);

  const handleAttemptClose = () => {
    setShowAreYouSure(true);
  };

  const handleConfirmClose = () => {
    setShowAreYouSure(false);
    handleToggleModal(); // Close the main modal
  };

  const spanStyles = currentDirection === "rtl" ? classes.shortSpan : null;

  const calculateLeftAmount = (receivedAmount) => {
    setInvoice((prev) => {
      const sanitizedFinalAmount = sanitizeNumber(amounts?.finalAmount);
      const sanitizedReceived = sanitizeNumber(receivedAmount);
      const calculatedLeft = sanitizedFinalAmount - sanitizedReceived;
      return {
        ...prev,
        leftAmount: calculatedLeft,
      };
    });
  };

  const handleInputChange = (e, type, state = "invoice") => {
    const { name, value } = e.target;

    // Handle change on receivedAmount input
    if (name === "receivedAmount") {
      const finalAmount = parseFloat(sanitizeNumber(amounts.finalAmount)); // Assuming amounts.finalAmount is the max limit
      let receivedValue = Number(value);
      if (receivedValue < 0) {
        receivedValue = 0;
      } else if (receivedValue > finalAmount) {
        receivedValue = finalAmount;
      }

      setInvoice((prev) => ({
        ...prev,
        receivedAmount: receivedValue,
        leftAmount: calculateLeftAmount(receivedValue),
      }));
    }

    // Handle change on customer inputs
    if (state === "selectedCustomer") {
      setSelectedCustomer((prev) => ({ ...prev, [name]: value }));
    }

    // Handle change on customer inputs
    else {
      if (type === "date") {
        setInvoice((prev) => ({ ...prev, [name]: new Date(value) }));
      } else if (type === "number") {
        setInvoice((prev) => ({
          ...prev,
          [name]: Number(value),
        }));
      } else {
        setInvoice((prev) => ({ ...prev, [name]: value }));
      }
    }
  };

  const handleDiscountChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;
    newValue = Number(value);
    if (newValue > 100) newValue = 100;
    if (newValue < 0) newValue = 0;

    setInvoice((prev) => {
      const newFinalAmount = calculatePercentage(
        sanitizeNumber(amounts?.totalPrice),
        newValue
      );

      const newLeftAmount = newFinalAmount - prev.receivedAmount;
      console.log("newFinalAmount", newFinalAmount);

      setAmounts((prevAmounts) => ({
        ...prevAmounts,
        finalAmount: toMoney(newFinalAmount),
        finalAmountInAr: toArabicWord(newFinalAmount),
        finalAmountInEn: toWords(newFinalAmount),
      }));

      return { ...prev, [name]: newValue, leftAmount: newLeftAmount };
    });
  };

  const handleItemsChanges = (items) => {
    setInvoice((prev) => {
      const totalPrice = items?.reduce(
        (total, item) => total + (parseFloat(item.totalPrice) || 0),
        0
      );

      const finalAmount = calculatePercentage(
        sanitizeNumber(totalPrice),
        prev.discount
      );

      setAmounts({
        totalPrice: toMoney(totalPrice),
        finalAmount: toMoney(finalAmount),
        finalAmountInAr: toArabicWord(finalAmount),
        finalAmountInEn: toWords(finalAmount),
      });

      const sanitizedReceived = sanitizeNumber(prev.receivedAmount);
      const leftAmount = finalAmount - sanitizedReceived;

      return {
        ...prev,
        items,
        leftAmount,
      };
    });
  };

  const handleUpdateInvoice = async () => {
    const payload = {
      // Invoice data
      id: invoice?.id,
      date: invoice?.date,
      notes: invoice?.notes,
      receivedAmount: parseFloat(sanitizeNumber(invoice.receivedAmount)),
      leftAmount: parseFloat(sanitizeNumber(invoice.leftAmount)),
      discount: parseFloat(sanitizeNumber(invoice.discount)),
      items: invoice?.items,
      invoiceType: selectedInvoiceType?.value,
      totalPrice: parseFloat(sanitizeNumber(amounts.totalPrice)),
      finalAmount: parseFloat(sanitizeNumber(amounts.finalAmount)),
      finalAmountInAr: amounts.finalAmountInAr,
      finalAmountInEn: amounts.finalAmountInEn,

      // Customer data
      customerName: selectedCustomer?.name,
      customerPhoneNumber: selectedCustomer?.phoneNumber,
      customerAddress: selectedCustomer?.address,
      customerId: selectedCustomer?.id,

      // Ids
      userId: parseInt(userData?.userId),
      organizationId: parseInt(userData?.organizationId),
    };

    if (!payload?.customerName || !payload?.totalPrice) {
      setError("emptyInputField");
      return;
    }

    const hasInvalidItems = invoice?.items?.some(
      (item) =>
        item?.name.trim() === "" || item?.quantity === 0 || item?.price === 0
    );

    if (hasInvalidItems) {
      setError("emptyInputField");
      return;
    }

    try {
      setLoading(true);
      const req = await axios.put(
        `${process.env.REACT_APP_URL}/editItemInvoice/${invoice?.id}`,
        payload
      );

      // For create
      if (req?.status === 201) {
        setLoading(false);
        handleToggleModal();
      }

      // For Edit
      if (req?.status === 200) {
        setLoading(false);
        handleToggleModal();

        const updatedInvoice = req?.data?.invoice;
        setData(updatedInvoice);
        setInvoice({
          date: updatedInvoice?.date,
          notes: updatedInvoice?.notes,
        });
      }
    } catch (err) {
      console.error("Failed to Update the invoice", error);
      if (err?.response && err?.response?.status === 400) {
        console.log("err.response.status");
        setError(err?.response?.data);
        console.log("err.response.data", err?.response?.data);
      } else {
        setError("networkError");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewInvoice = () => {
    const payloadForPreview = {
      // ...invoice,
      id: invoice?.id,
      date: invoice?.date,
      invoiceType: selectedInvoiceType?.value,
      notes: invoice?.notes,
      items: invoice?.items,
      customer: selectedCustomer,
      totalPrice: parseFloat(sanitizeNumber(amounts.totalPrice)),
      finalAmount: parseFloat(sanitizeNumber(amounts.finalAmount)),
      finalAmountInAr: amounts?.finalAmountInAr,
      finalAmountInEn: amounts?.finalAmountInEn,
      receivedAmount: parseFloat(sanitizeNumber(invoice.receivedAmount)),
      leftAmount: parseFloat(sanitizeNumber(invoice.leftAmount)),
      discount: parseFloat(invoice.discount),
      userId: parseInt(userData?.userId),
      organizationId: parseInt(userData?.organizationId),
    };

    navigate(`/previewItemInvoice/${invoice?.id}`, {
      state: { data: payloadForPreview },
    });
  };

  return (
    <>
      <Modal
        title={t("editInvoice")}
        onClose={handleAttemptClose}
        error={error === "emptyInputField" ? t("emptyInputFields") : t(error)}
        footer={
          <>
            {/* {error ? (
              <ErrorMessage>
                {error === "emptyInputField" ? t("emptyInputFields") : t(error)}
              </ErrorMessage>
            ) : null} */}
            {loading ? <Loader /> : null}
            <section className="edit-user-actions">
              <CustomButton
                title={t("save")}
                onClick={handleUpdateInvoice}
                icon="save"
                iconSize={16}
                disabled={loading}
              />
              <CustomButton
                title={t("view")}
                onClick={handlePreviewInvoice}
                icon="view"
                iconSize={20}
                variant="blank"
                disabled={loading}
              />
              <CustomButton
                title={t("cancel")}
                onClick={handleAttemptClose}
                variant="blank"
                icon="close"
                iconSize={14}
              />
            </section>
          </>
        }
      >
        <form

        // onSubmit={handleCreateSubCompany}
        >
          {error ? (
            <ErrorBox>
              {error === "emptyInputField" ? t("emptyInputFields") : t(error)}
            </ErrorBox>
          ) : null}
          <section className="o-column o-column--gap-size_300">
            <div className={classes.inputContainer}>
              <span className={spanStyles}>{t("invoiceNumber")}: </span>
              <CustomInput
                name="id"
                value={invoice?.id}
                onChange={handleInputChange}
                flex={true}
                loading={loadingInput}
                readonly={true}
              />
            </div>

            <div className={classes.inputContainer}>
              <span className={spanStyles}>{t("invoiceDate")}: </span>
              <CustomInput
                name="date"
                value={formatDate(invoice?.date)}
                type="date"
                onChange={(e) => handleInputChange(e, "date")}
                flex={true}
              />
            </div>
          </section>
          <SectionDivider title="customerInfo" />
          <section className="o-column o-column--gap-size_300">
            {error && (
              <div className="o-row o-row--horizontal-align_center o-row--vertical-align_center">
                {/* <ErrorBox>{t(errorMessage)}</ErrorBox> */}
              </div>
            )}
            <div className={classes.inputContainer}>
              <span className={spanStyles}>{t("customerName")}: </span>
              <HotCustomInput
                initOptions={[]}
                selected={selectedCustomer}
                setSelected={(customer) =>
                  handleSelectCustomer(customer, "noRequest")
                }
                optionsName={"customers"}
                getData={getCustomers}
                flex
                errorText={error}
              />
            </div>

            <div className={classes.inputContainer}>
              <span className={spanStyles}>{t("phoneNumber")}: </span>
              <CustomInput
                name="phoneNumber"
                value={selectedCustomer?.phoneNumber}
                onChange={(e) =>
                  handleInputChange(e, "string", "selectedCustomer")
                }
                flex={true}
                errorText={error}
              />
            </div>

            <div className={classes.inputContainer}>
              <span className={spanStyles}>{t("address")}: </span>
              <CustomInput
                name="address"
                value={selectedCustomer?.address}
                onChange={(e) =>
                  handleInputChange(e, "string", "selectedCustomer")
                }
                flex={true}
              />
            </div>
          </section>
          <ItemInvoiceCard
            sendDataToParent={handleItemsChanges}
            error={error}
            data={data?.items}
          />
          <section className="o-column o-column--gap-size_300 u-margin--top_600">
            <div className="o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width">
              <div className={classes.inputContainer}>
                <span className={spanStyles}>{t("discount")}: </span>
                <CustomInputWithUnit
                  name="discount"
                  value={invoice?.discount}
                  onChange={handleDiscountChange}
                  onKeyDown={onlyNums}
                  flex={true}
                  unit="%"
                />
              </div>
            </div>

            <div className="u-margin--bottom_400">
              <BalanceBoxes
                boxes={[
                  {
                    title: "totalAmount",
                    value: `${amounts.totalPrice} ${
                      selectedCustomer?.currency || t("IQD")
                    }`,
                  },
                  {
                    title: "netAmount",
                    value: `${amounts.finalAmount} ${
                      selectedCustomer?.currency || t("IQD")
                    }`,
                  },
                ]}
              />
            </div>

            <div className={classes.inputContainer}>
              <span className={spanStyles}>{t("invoiceType")}: </span>

              <Dropdown
                options={[
                  { title: t("cash"), value: "cash" },
                  { title: t("debt"), value: "debt" },
                ]}
                selected={selectedInvoiceType}
                setSelected={setSelectedInvoiceType}
                flex
              />
            </div>
            <div className={classes.inputContainer}>
              <span className={spanStyles}>{t("received")}: </span>
              <CustomInput
                name="receivedAmount"
                value={invoice?.receivedAmount}
                onChange={handleInputChange}
                onKeyDown={onlyNums}
                flex={true}
              />
            </div>
            <div className={classes.inputContainer}>
              <span className={spanStyles}>{t("left")}: </span>
              <CustomInput
                name="leftAmount"
                value={invoice?.leftAmount}
                // onChange={handleInputChange}
                // onKeyDown={onlyNums}
                flex={true}
                readonly={true}
              />
            </div>
            <div className={classes.inputContainer}>
              <span className={`${spanStyles} ${classes.notesClass}`}>
                {t("notes")}:{" "}
              </span>
              <CustomInput
                name="notes"
                type="textarea"
                value={invoice?.notes}
                onChange={handleInputChange}
                flex={true}
              />
            </div>
          </section>
        </form>
      </Modal>

      {showAreYouSure ? (
        <AreYouSureModal
          shouldCloseModal={shouldCloseModal({
            ...invoice,
            selectedCustomer,

            // * Ignore the below inputs, because it's always valid
            id: "",
            date: "",
          })}
          onClose={() => setShowAreYouSure(false)}
          onConfirm={handleConfirmClose}
          confirmIconType="whiteCheck"
        />
      ) : null}
    </>
  );
}
